import type { ProgressEvent } from 'superagent';

import Locator from '../../../../../shared/Locator';
import useUploadAndBuildArchive from '../../../../hooks/api/upload/useUploadAndBuildArchive';
import useMutation from '../../../../ReactQuery/useMutation';

/**
 * Parameters for uploading and building an archive dependency.
 */
export type UploadAndBuildArchiveDependencyParams = {
  /**
   * The archive file to be uploaded and built as a dependency.
   */
  file: File;
  /**
   * The name (project) of the dependency being uploaded.
   */
  name: string;
  /**
   * The version (revision) of the dependency being uploaded.
   */
  version: string;
  /**
   * Optional homepage URL for the dependency being uploaded.
   */
  homepage?: string;
  /**
   * Optional description for the dependency being uploaded.
   */
  description?: string;
  /**
   * Callback function that is called to update the progress bar during an archive upload.
   */
  onArchiveUploadProgress?: (progressEvent: ProgressEvent) => void;
};

/**
 * Hook for uploading and building an archive dependency.
 */
export default function useUploadAndBuildArchiveDependency(parentLocator: string) {
  const { mutateAsync: uploadAndBuildArchive } = useUploadAndBuildArchive();
  return useMutation({
    mutationFn: async (params: UploadAndBuildArchiveDependencyParams) => uploadAndBuildArchive({
      file: params.file,
      name: params.name,
      version: params.version,
      options: {
        title: params.name,
        projectURL: params.homepage,
        description: params.description,
        // This archive upload is for a dependency, not a top-level project.
        dependency: true,
        // When uploading manual dependencies to a project, be sure to rebuild the parent project's package cache.
        rebuildPackageCacheForProject: Locator.parsePackageString(parentLocator),
      },
      onArchiveUploadProgress: params.onArchiveUploadProgress,
    }),
  });
}
